import React, { PropTypes } from 'react';
import { routerShape, withRouter } from 'react-router';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import { FormattedMessage } from 'react-intl';
import { isConjunctiveField } from '@bibliocommons/utils-filter';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import * as Drawer from '@bibliocommons/base-drawer';
import { TextButton } from '@bibliocommons/base-buttons';

import CheckboxField from '../CheckboxField';
import FormatField from '../FormatField';
import LanguageField from '../LanguageField';
import SimpleField from '../SimpleField';
import TagGroupField from '../TagGroupField';
import SingleBranchStatusField from '../../../search/SingleBranchStatusField';
import { FIELD_ORDER } from '../../../../constants/FacetConstants';

import getToggledFilter from '../../../../helpers/facets/getToggledFilter';
import formatFiltersForQuery from '../../../../helpers/facets/formatFiltersForQuery';

import './FieldList.scss';

export class FieldList extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('renderFields', 'handleToggleFilter', 'handleToggleExpandedField');
  }

  handleToggleFilter(fieldId, currentFilter) {
    const { activeFilters, paginationField, location, router, transformQuery } = this.props;

    let query = {
      ...location.query,
      [paginationField]: undefined,
      ...formatFiltersForQuery(activeFilters),
      ...getToggledFilter(fieldId, currentFilter, activeFilters)
    };

    if (transformQuery) {
      query = transformQuery(query);
    }

    router.push({ ...location, query });
  }

  handleToggleExpandedField(fieldId, currentFilter) {
    this.handleToggleFilter(fieldId, currentFilter);
  }

  isFieldExpanded(id) {
    return this.props.activeFilters.has(id);
  }

  renderFields(fieldId) {
    const handleToggleFilter = this.props.handleToggleFilter || this.handleToggleFilter;
    const handleToggleExpandedField = this.props.handleToggleExpandedField || this.handleToggleExpandedField;

    let fieldTag;
    switch (fieldId) {
      case 'SEARCH_SCOPE': {
        fieldTag = this.props.renderSearchScope ? this.props.renderSearchScope() : null;
        break;
      }
      case 'IS_PRIVATE':
      case 'I_OWN_THIS': {
        const field = this.props.fields.find((f) => f.get('id') === fieldId);
        const fieldFilter = field && field.get('fieldFilters').find(filter => `${filter.get('value')}` === 'true');

        if (!fieldFilter) {
          return <noop key={fieldId} />;
        }

        fieldTag = (
          <CheckboxField
            key={fieldId}
            fieldId={fieldId}
            filter={fieldFilter}
            handleToggleFilter={handleToggleFilter} />
        );
        break;
      }
      case 'TAGS': {
        const genreTagField = this.props.fields.find((f) => f.get('id') === 'TAG_GENRE');
        const toneTagField = this.props.fields.find((f) => f.get('id') === 'TAG_TONE');
        const aboutTagField = this.props.fields.find((f) => f.get('id') === 'TAG_ABOUT');
        const personalTagField = this.props.fields.find((f) => f.get('id') === 'TAG_PERSONAL');
        if (!(genreTagField || toneTagField || aboutTagField)) {
          return (<noop key="tagGroupField" />);
        }
        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <TagGroupField
              key="TAG_GROUP"
              genreTagField={genreTagField}
              genreExpanded={this.isFieldExpanded('TAG_GENRE')}
              toneTagField={toneTagField}
              toneExpanded={this.isFieldExpanded('TAG_TONE')}
              aboutTagField={aboutTagField}
              aboutExpanded={this.isFieldExpanded('TAG_ABOUT')}
              personalTagField={personalTagField}
              personalExpanded={this.isFieldExpanded('TAG_PERSONAL')}
              handleToggleFilter={handleToggleFilter}
              handleExpandField={this.props.handleExpandField}
              activeFilters={this.props.activeFilters}
              handleCloseExpandField={this.props.handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField} />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
      case 'LANGUAGES': {
        let field = null;
        const primaryLanguage = this.props.fields.find((f) => f.get('id') === 'PRIMARY_LANGUAGE');
        const language = this.props.fields.find((f) => f.get('id') === 'LANGUAGE');

        if (!(primaryLanguage || language)) {
          return <noop key={fieldId} />;
        }

        if (!primaryLanguage && language) {
          field = language;
        }

        if (primaryLanguage && !language) {
          field = primaryLanguage;
        }

        if (field) {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <SimpleField
                key={field.get('id')}
                label="LANGUAGE.field"
                fieldId={field.get('id')}
                hasMore={field.get('hasMore')}
                filters={field.get('fieldFilters')}
                handleExpandField={this.props.handleExpandField}
                handleToggleFilter={handleToggleFilter}
                activeFilters={this.props.activeFilters}
                initialIsExpanded={this.isFieldExpanded(field.get('id'))}
                handleCloseExpandField={this.props.handleCloseExpandField}
                handleToggleExpandedField={handleToggleExpandedField} />
            </AboveTheFoldOnlyServerRender>
          );
        } else {
          fieldTag = (
            <AboveTheFoldOnlyServerRender skip>
              <LanguageField
                key="LANGUAGES"
                primaryLanguage={primaryLanguage}
                language={language}
                handleToggleFilter={handleToggleFilter}
                handleExpandField={this.props.handleExpandField}
                handleCloseExpandField={this.props.handleCloseExpandField}
                activeFilters={this.props.activeFilters}
                handleToggleExpandedField={handleToggleExpandedField}
                handleLanguageToggle={this.props.handleLanguageToggle}
                transformQuery={this.props.transformQuery}
                router={this.props.router} />
            </AboveTheFoldOnlyServerRender>
          );
        }
        break;
      }
      case 'FORMAT': {
        const formatField = this.props.fields.find((f) => f.get('id') === fieldId);

        if (!formatField) {
          return <noop key={fieldId} />;
        }

        fieldTag = (
          <FormatField
            key="FORMAT"
            fieldId={formatField.get('id')}
            hasMore={formatField.get('hasMore')}
            filters={formatField.get('fieldFilters')}
            handleToggleFilter={handleToggleFilter}
            activeFilters={this.props.activeFilters} />
          );
        break;
      }
      case 'STATUS':
        if (this.props.singleBranch) {
          const field = this.props.fields.find((f) => f.get('id') === fieldId);
          if (typeof field === 'undefined') {
            return (<noop key={fieldId} />);
          }
          fieldTag = (
            <SingleBranchStatusField
              key={field.get('id')}
              label={`${field.get('id')}.field`}
              fieldId={field.get('id')}
              filter={field.get('fieldFilters').first()}
              handleToggleFilter={handleToggleFilter}
              activeFilters={this.props.activeFilters} />
          );
          break;
        }
        // STATUS must be the last case before default so it can fallthrough on non single branch libraries
      default: { // eslint-disable-line no-fallthrough
        const field = this.props.fields.find((f) => f.get('id') === fieldId);
        if (typeof field === 'undefined') {
          return (<noop key={fieldId} />);
        }
        fieldTag = (
          <AboveTheFoldOnlyServerRender skip>
            <SimpleField
              isConjunctive={isConjunctiveField(fieldId)}
              key={field.get('id')}
              label={`${field.get('id')}.field`}
              fieldId={field.get('id')}
              hasMore={field.get('hasMore')}
              filters={field.get('fieldFilters')}
              handleExpandField={this.props.handleExpandField}
              handleToggleFilter={handleToggleFilter}
              activeFilters={this.props.activeFilters}
              initialIsExpanded={this.isFieldExpanded(field.get('id'))}
              handleCloseExpandField={this.props.handleCloseExpandField}
              handleToggleExpandedField={handleToggleExpandedField} />
          </AboveTheFoldOnlyServerRender>
        );
        break;
      }
    }

    if (!fieldTag) {
      return null;
    }

    return (
      <li key={fieldId} className="field">
        {fieldTag}
      </li>
    );
  }


  render() {
    return (
      <div className="cp-field-list">
        <h3>
          <FormattedMessage id="Facets.refineBy" />
          <small className="pull-right">
            <Drawer.Toggle drawerActions={this.props.drawerActions}>
              <TextButton handleClick={this.props.drawerActions.toggle}>
                <span className="icon icon-left-open" />
                Close
              </TextButton>
            </Drawer.Toggle>
          </small>
        </h3>
        <ul>
          {FIELD_ORDER.map(this.renderFields)}
        </ul>
      </div>
    );
  }
}

FieldList.propTypes = {
  activeFilters: ImmutablePropTypes.map.isRequired,
  fields: ImmutablePropTypes.list.isRequired,
  handleExpandField: PropTypes.func.isRequired,
  handleToggleFilter: PropTypes.func,
  handleToggleExpandedField: PropTypes.func,
  handleCloseExpandField: PropTypes.func,
  handleLanguageToggle: PropTypes.func,
  paginationField: PropTypes.string,
  renderSearchScope: PropTypes.func,
  transformQuery: PropTypes.func,
  singleBranch: PropTypes.bool,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape
};

FieldList.defaultProps = {
  paginationField: 'page',
  singleBranch: false
};

export default withRouter(FieldList);



// WEBPACK FOOTER //
// ./app/components/shared/Facets/FieldList/FieldList.jsx